import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch, API } from "@/utils/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 12) {
      setError("La contraseña debe tener al menos 12 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`${API}/auth/reset-password`, {
        method: "POST",
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      if (response.ok) {
        setDone(true);
      } else {
        const data = await response.json().catch(() => null);
        setError(
          data?.detail || "El enlace no es válido o ha caducado. Pide uno nuevo."
        );
      }
    } catch {
      setError("Error de conexión. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 p-8"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8 text-center">
          LiquidArc
        </h1>

        {!token && (
          <div className="bg-white border border-red-200 rounded-lg px-6 py-8 space-y-4 text-center" data-testid="reset-password-no-token">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              Enlace no válido
            </h2>
            <p className="text-sm text-red-600">
              Falta el token en el enlace. Pide restablecer la contraseña de nuevo.
            </p>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="w-full h-12 font-medium"
              data-testid="reset-password-back-btn"
            >
              Volver a iniciar sesión
            </Button>
          </div>
        )}

        {token && !done && (
          <div className="bg-white border border-slate-200 rounded-lg px-6 py-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4 text-center">
              Nueva contraseña
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new-password">Contraseña nueva</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 12 caracteres"
                  required
                  minLength={12}
                  data-testid="reset-password-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Al menos 12 caracteres. Puedes usar una frase fácil de recordar.
                </p>
              </div>
              <div>
                <Label htmlFor="confirm-password">Repite la contraseña</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  required
                  minLength={12}
                  data-testid="reset-password-confirm-input"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                data-testid="reset-password-submit-btn"
              >
                {loading ? "Guardando..." : "Cambiar contraseña"}
              </Button>
            </form>
          </div>
        )}

        {token && done && (
          <div className="bg-white border border-emerald-200 rounded-lg px-6 py-8 space-y-4 text-center" data-testid="reset-password-success">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              Contraseña cambiada
            </h2>
            <p className="text-sm text-slate-600">
              Ya puedes iniciar sesión con tu contraseña nueva.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              data-testid="reset-password-login-btn"
            >
              Ir a iniciar sesión
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
